import { type ByeReason, type MasterMessage, PROTOCOL_VERSION, type ScreenContext } from '@iws/pos-companion'
import { useSyncExternalStore } from 'react'

import { type DisplayMessage, lastDisplayMessage, onDisplayMessage } from '@/services/customerDisplay'

import { openLink } from './link'
import { routeCompanionScan } from './scans'

export const PAIR_PERMISSION = 'pos:pair_companion'

export type MasterState =
  | { kind: 'off'; error?: string }
  | { kind: 'connecting' }
  | { kind: 'asking'; device: string }
  | { kind: 'linked'; device: string; online: boolean }

export interface MasterInfo {
  terminal_no: number
  terminal_name: string
  branch: string
  staff: string
}

let state: MasterState = { kind: 'off' }
const subs = new Set<() => void>()
const set = (s: MasterState) => {
  state = s
  subs.forEach((f) => f())
}

let link: ReturnType<typeof openLink> | null = null
let allowed = false
let device = ''
let screen: ScreenContext = 'other'
let info: MasterInfo | null = null
let unwatch: (() => void) | null = null

const cartOf = (m: DisplayMessage | null): MasterMessage => {
  if (m?.type !== 'cart') return { type: 'cart', lines: [], subtotal: 0, discount: 0, vat: 0, total: 0 }
  const t = m.totals
  return {
    type: 'cart',
    lines: m.lines.map((l) => ({ name: l.name, qty: l.quantity, unit_price: l.price, discount: Math.max(l.quantity * l.price - l.net, 0), total: l.net })),
    subtotal: t.subtotal,
    discount: t.lineDiscounts + t.orderDiscount,
    vat: t.tax,
    total: t.total,
    customer: m.customer,
  }
}

const send = (m: MasterMessage) => allowed && link?.send(m)

const welcome = () => {
  if (!info) return
  send({ type: 'welcome', v: PROTOCOL_VERSION, ...info })
  send({ type: 'context', screen })
  send(cartOf(lastDisplayMessage()))
}

const onScan = async (id: string, code: string) => {
  const before = lastDisplayMessage()
  const added = await routeCompanionScan(code)
  if (added === null) return send({ type: 'result', id, ok: false, title: 'Open Sale on the device' })
  const after = lastDisplayMessage()
  const line = after?.type === 'cart' ? after.lines.at(-1) : undefined
  send(
    added && after !== before
      ? { type: 'result', id, ok: true, title: line?.name ?? 'Added', total: after?.type === 'cart' ? after.totals.total : undefined }
      : { type: 'result', id, ok: added, title: added ? 'Sent' : 'Not added', detail: added ? undefined : 'Check the device' }
  )
}

/** pos-web as master: connects to a pos-app companion that shows a code (POS_COMPANION.md §3, §9). */
export const companionMaster = {
  subscribe: (f: () => void) => {
    subs.add(f)
    return () => void subs.delete(f)
  },
  get: () => state,

  connect(code: string, who: MasterInfo) {
    this.stop(null)
    info = who
    set({ kind: 'connecting' })
    try {
      link = openLink(code, 'master', {
        onReady: () => undefined,
        onOffline: () => state.kind === 'linked' && set({ ...state, online: false }),
        onEnded: (message) => {
          link = null
          allowed = false
          set({ kind: 'off', error: message === 'Unpaired' ? undefined : message })
        },
        onEvent: (e) => {
          if (e.kind === 'joined') {
            device = e.hello.device_name
            if (allowed) {
              welcome()
              set({ kind: 'linked', device, online: true })
            } else set({ kind: 'asking', device })
          } else if (e.kind === 'from_companion' && e.message.type === 'scan') {
            if (allowed) void onScan(e.message.id, e.message.code)
          } else if (e.kind === 'left' && state.kind === 'linked') set({ ...state, online: false })
        },
      })
    } catch (e) {
      set({ kind: 'off', error: e instanceof Error ? e.message : 'Invalid code' })
      return
    }
    unwatch = onDisplayMessage((m) => send(cartOf(m)))
  },

  allow() {
    if (state.kind !== 'asking') return
    allowed = true
    welcome()
    set({ kind: 'linked', device, online: true })
  },

  deny() {
    allowed = true
    send({ type: 'bye', reason: 'denied' })
    this.stop(null)
  },

  setScreen(s: ScreenContext) {
    if (s === screen) return
    screen = s
    send({ type: 'context', screen: s })
  },

  stop(reason: ByeReason | null = 'unpaired') {
    if (reason) send({ type: 'bye', reason })
    unwatch?.()
    unwatch = null
    allowed = false
    link?.stop()
    link = null
    set({ kind: 'off' })
  },
}

export const useCompanionMaster = () => useSyncExternalStore(companionMaster.subscribe, companionMaster.get)
